/**
 * REK-2114: stamp `profiles.last_login_at` on an authenticated arrival.
 *
 * WHY THIS EXISTS: nothing in the system owned a last-login timestamp. Braze's
 * apparent one was emergent — under Supabase auth, `login` fired on every
 * `SIGNED_IN` (~3-4x/day), so "last performed login" tracked recency by
 * accident. Clerk sessions live 10 years, so the once-per-session `login` event
 * now fires ~1x per user, forever. `profiles.last_activity_at` looks like the
 * answer but has never had a writer (see the column comment in
 * `20260729204500_rek_2114_profiles_last_login_at.sql`).
 *
 * The write goes through the `touch_last_login()` RPC rather than a direct
 * PostgREST UPDATE so the timestamp is the SERVER clock, the user id comes from
 * the verified JWT, and — the load-bearing part — the once-per-hour throttle is
 * enforced in SQL. That is what makes "at most one write per hour per user"
 * true across page loads, browsers, and devices.
 *
 * The in-memory guard below is a separate, weaker thing: it stops a single page
 * session from re-asking within the hour. It deliberately does NOT read the
 * stored value first — an extra round trip would cost the same as the RPC, and
 * folding the column into the auth path's fail-closed profile query would make
 * sign-in depend on a marketing recency field.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { safeError } from '@/lib/safeLog';

/**
 * Minimum gap between two stamp attempts for the same user. Mirrors the
 * `interval '1 hour'` guard inside `touch_last_login()`; if you change one,
 * change both.
 */
export const LAST_LOGIN_THROTTLE_MS = 60 * 60 * 1000;

/**
 * `profiles.id` -> when this page session last asked the server to stamp.
 * Module-level (not a hook) so it holds across provider re-mounts within a
 * page. At most a handful of entries: one per user seen in this page session.
 */
const attemptedAtByUser = new Map<string, number>();

/**
 * `profiles.id` -> the RPC currently in flight for that user, so a concurrent
 * arrival JOINS it instead of racing past it. Without this, a second grant
 * effect (React StrictMode's double-invoke, a provider remount) would see the
 * optimistic mark below, return immediately, and run the Braze attribute sync
 * while the first stamp was still uncommitted — publishing a stale value.
 */
const inFlightByUser = new Map<string, Promise<string | null>>();

/** Test seam — both maps are module state, so suites must reset them. */
export function __resetLastLoginThrottleForTests(): void {
  attemptedAtByUser.clear();
  inFlightByUser.clear();
}

/**
 * Whether this page session should ask the server to stamp for `userId`.
 *
 * A `false` here only means "we already asked recently"; the authoritative
 * throttle is the SQL guard inside `touch_last_login()`.
 */
export function shouldStampLastLogin(userId: string, now: number = Date.now()): boolean {
  const attemptedAt = attemptedAtByUser.get(userId);
  return attemptedAt === undefined || now - attemptedAt >= LAST_LOGIN_THROTTLE_MS;
}

/**
 * Stamp `profiles.last_login_at` for the caller.
 *
 * Best-effort: every failure is logged and swallowed, because a marketing
 * recency signal must never break a sign-in. Resolves to the timestamp that was
 * written, or `null` when nothing was written — the server's own throttle
 * suppressed it, the session was unauthenticated, or the call failed.
 *
 * The Supabase client is INJECTED (never imported at module load) so unit tests
 * can pass a mock and CI doesn't need `VITE_SUPABASE_URL` at import time —
 * same convention as `braze-user-sync.ts`.
 */
export async function stampLastLogin(
  client: SupabaseClient,
  userId: string,
): Promise<string | null> {
  if (!userId) return null;

  // Await an existing call rather than returning early past it, so a caller
  // that chains work on the stamp (the Braze attribute sync) never observes
  // the pre-stamp value.
  const inFlight = inFlightByUser.get(userId);
  if (inFlight) return inFlight;

  if (!shouldStampLastLogin(userId)) return null;

  // Optimistic mark BEFORE the call so a re-entrant arrival burst (provider
  // re-mount, tab regain) cannot fire two RPCs; rolled back on failure so a
  // later arrival in this same page session retries. Same shape as the
  // per-session guards in `post-login-effects.ts`.
  attemptedAtByUser.set(userId, Date.now());

  const request = requestStamp(client, userId).finally(() => {
    inFlightByUser.delete(userId);
  });
  inFlightByUser.set(userId, request);
  return request;
}

/** The RPC call itself. Never rejects — see `stampLastLogin`'s contract. */
async function requestStamp(
  client: SupabaseClient,
  userId: string,
): Promise<string | null> {
  try {
    const { data, error } = await client.rpc('touch_last_login');
    if (error) {
      attemptedAtByUser.delete(userId);
      safeError('[last-login] touch_last_login failed (non-fatal):', error);
      return null;
    }
    // The RPC returns NULL when its own throttle suppressed the write, so the
    // generated non-nullable scalar return type is optimistic here.
    return typeof data === 'string' ? data : null;
  } catch (e) {
    attemptedAtByUser.delete(userId);
    safeError('[last-login] touch_last_login threw (non-fatal):', e);
    return null;
  }
}
